/**
 * Tracked-change AUTHOR TOKEN — the deal-doc suggestion ⇄ relational-row link.
 *
 * PropDocs keeps `document_suggestions` as the source of truth for a deal's
 * tracked changes: the row carries `external_id`, `author_id`, `status`, and the
 * server derives `authorRole` (creator | receiver | lost) from `author_id` vs the
 * document's team/receiver_team. The accept/reject permission rule
 * (`decideSuggestion` in @propdocs-editor/review) needs that ROLE.
 *
 * But OOXML tracked changes (`w:ins` / `w:del`) carry only `w:id` (a numeric,
 * per-document counter) and `w:author` (free text) — there is no standard slot
 * for an external id. Wrapping every tracked run in an SDT to smuggle one is
 * invasive and unproven through the importer.
 *
 * So we use `w:author` itself, which round-trips VERBATIM:
 *   - import:  `runParser.ts` reads the attribute into `TrackedChangeEntry.author`
 *   - export:  `runSerializer.ts` writes `change.info.author` back, escaped only
 *
 * Encoding a structured token there gives EXACT re-linkage with zero schema,
 * importer, or serializer changes. The editor decodes it for the permission gate
 * (external id → row → role) and renders only `display` in the UI.
 *
 * Format: `authorId␟externalId␟Display Name`
 * The separator is U+241F (SYMBOL FOR UNIT SEPARATOR) — a printable glyph that
 * never occurs in real names, survives XML escaping, and (unlike a raw control
 * char) is legal in an XML attribute value.
 *
 * A plain author string (any legacy / externally-authored .docx, e.g. one edited
 * in Word) decodes to `{ display: <the string> }` with no ids — callers fall back
 * to name-based display and an ungated (host-decided) permission answer.
 */

/** U+241F — printable, XML-safe, never present in a human name. */
export const AUTHOR_TOKEN_SEP = '␟';

export interface AuthorToken {
  /** PropDocs user id that authored the change (maps to document_suggestions.author_id). */
  authorId?: string;
  /** CKE/PropDocs suggestion external id (maps to document_suggestions.external_id). */
  externalId?: string;
  /** Human-readable name — the ONLY part ever shown in the UI. */
  display: string;
}

/**
 * Build the `w:author` value for a tracked change. Omitted ids are encoded as
 * empty segments so the shape stays fixed and decoding stays unambiguous.
 * When BOTH ids are absent the plain display name is returned (no token), so we
 * never bloat documents that have nothing to link.
 */
export function encodeAuthorToken(token: AuthorToken): string {
  const authorId = token.authorId ?? '';
  const externalId = token.externalId ?? '';
  const display = token.display ?? '';
  if (!authorId && !externalId) return display;
  return `${authorId}${AUTHOR_TOKEN_SEP}${externalId}${AUTHOR_TOKEN_SEP}${display}`;
}

/**
 * Parse a `w:author` value. Always returns a usable `display` — a non-token
 * string (Word-authored change, legacy doc) passes through unchanged.
 */
export function decodeAuthorToken(author: string | null | undefined): AuthorToken {
  const raw = author ?? '';
  if (!raw.includes(AUTHOR_TOKEN_SEP)) return { display: raw };
  const [authorId = '', externalId = '', ...rest] = raw.split(AUTHOR_TOKEN_SEP);
  return {
    authorId: authorId || undefined,
    externalId: externalId || undefined,
    // Re-join so a display name that somehow contains the separator survives.
    display: rest.join(AUTHOR_TOKEN_SEP),
  };
}

/** Convenience: the name to SHOW for a tracked change's author. */
export function authorDisplayName(author: string | null | undefined): string {
  return decodeAuthorToken(author).display;
}
